import { useCallback, useMemo } from 'react'
import type { Product } from '~/types/Product'
import type MyDesign from '~/types/MyDesign'
import { fetchMyDesignList } from '~/api/myDesign'
import { useSession } from '~/hooks/useSession'
import {
  useLocalMyDesigns,
  type LocalMyDesign
} from '~/hooks/useLocalMyDesigns'

export type MyDesignEntry =
  | { origin: 'server'; data: MyDesign }
  | { origin: 'local'; data: LocalMyDesign }

export function useMyDesignListForProduct(product: Product) {
  const session = useSession()
  const local = useLocalMyDesigns()
  const accessToken = session.isLoggedIn ? session.accessToken.token : undefined

  const list = useCallback(async (): Promise<MyDesignEntry[]> => {
    const locals = await local.listByProduct(product.id, product.version)
    const localEntries: MyDesignEntry[] = locals.map((d) => ({
      origin: 'local',
      data: d
    }))

    if (!session.isLoggedIn) {
      return localEntries
    }

    const servers = await fetchMyDesignList(
      accessToken as string,
      undefined,
      product.series.id
    )
    const filtered = servers.filter((d) => d.product.id === product.id)
    const serverEntries: MyDesignEntry[] = filtered.map((d) => ({
      origin: 'server',
      data: d
    }))

    const merged = [...serverEntries, ...localEntries]
    return merged.sort((a, b) => {
      const aDate =
        a.origin === 'server'
          ? Date.parse(a.data.updateAt)
          : Date.parse(a.data.updatedAt)
      const bDate =
        b.origin === 'server'
          ? Date.parse(b.data.updateAt)
          : Date.parse(b.data.updatedAt)
      return bDate - aDate
    })
  }, [
    local,
    product.id,
    product.version,
    product.series.id,
    session.isLoggedIn,
    accessToken
  ])

  return useMemo(() => ({ list }), [list])
}
